import axios from 'axios';
import { Helpers } from '../helpers/Helpers';
import { ColumnChooserModel } from '../models/common/ColumnChooserModel';
import { ColumnFilterModel } from '../models/common/ColumnFilterModel';

/** Aylık sabit faturalama (depo kirası + kiralık personel, 08.10.2026). */

export const SABIT_GELIR_TURLERI = [
    { value: 1, label: 'Kira' },
    { value: 2, label: 'Personel' },
];

export const sabitGelirTurAdi = (tur: number | undefined | null) => tur === 1 ? 'Kira' : tur === 2 ? 'Personel' : '';

export const DONEM_AYLARI = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

export const donemAdi = (yil?: number | null, ay?: number | null) => yil && ay ? `${DONEM_AYLARI[ay - 1]} ${yil}` : '';

export const tl = (n: number | undefined | null) => (n ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export { sabitGelirPaylari } from './sabitGelirHesap';

export interface SabitGelirDagitim {
    sabitGelirDagitimId?: string
    companyPoId: string
    oran: number
    tutar?: number | null
    customerId?: string | null
    customerName?: string | null
    divisionName?: string | null
    poName?: string | null
    poNo?: string | null
}

export interface SabitGelirKarti {
    sabitGelirKartiId: string
    tur: number
    turAdi: string
    ad?: string | null
    ambarAgreementSchemeId: string
    agreementName?: string | null
    agreementRefNo?: string | null
    bedel?: number | null
    agreementStartDateStr?: string | null
    agreementEndDateStr?: string | null
    personnelId?: string | null
    personnelName?: string | null
    startDateStr?: string | null
    endDateStr?: string | null
    startDate?: string | null
    endDate?: string | null
    isCustomActive: boolean
    toplamOran: number
    musteriler?: string | null
    dagitimSayisi?: number
    createdDate?: string | null
    createdUser?: string | null
    updatedDate?: string | null
    updatedUser?: string | null
    dagitimlar: SabitGelirDagitim[]
    /** Liste ekranında satır seçimi (yalnız ekranda). */
    isSelected?: boolean
}

/** Standart liste ekranı isteği (09.10.2026): sayfalama, sıralama, kolon filtreleri, Excel. */
export interface SabitGelirListIstegi {
    searchTerm: string
    skip: number
    take: number
    currentPage: number
    sortColumn?: string
    sortDirection?: string
    columnChooser: ColumnChooserModel[] | undefined
    columnFilters: ColumnFilterModel[]
    ids?: string[]
}

export const initialSabitGelirListIstegi: SabitGelirListIstegi = {
    searchTerm: '',
    skip: 0,
    take: 50,
    currentPage: 1,
    sortColumn: undefined,
    sortDirection: undefined,
    columnChooser: undefined,
    columnFilters: []
}

export interface SabitGelirListSonucu {
    list: SabitGelirKarti[]
    totalRecordCount: number
}

export interface SabitGelirPoSatiri {
    companyPoId: string
    customerId?: string | null
    customerName?: string | null
    divisionName?: string | null
    poName?: string | null
    poNo?: string | null
    etiket: string
    tevkifatli: boolean
}

export interface SabitGelirSablon {
    ambarAgreementSchemeId: string
    name?: string | null
    guncelBedel?: number | null
    /** Şablonun hizmet kalemi; dinamikse boş ve hizmet dağıtım satırının PO'sundan gelir. */
    hizmetAdi?: string | null
    dinamik?: boolean
    currencyTypeName?: string | null
}

export interface SabitGelirSatir {
    divisionName?: string | null
    poName?: string | null
    companyPoId: string
    aciklama?: string | null
    /** Personelde kişi sayısı (paylar eşitse), kirada 1. */
    miktar: number
    birimFiyat: number
    tutar: number
    kdvOrani: number
    kdv: number
    toplam: number
    currencyTypeName?: string | null
    tevkifatli: boolean
    faturalandi: boolean
}

export interface SabitGelirHesapSonucu {
    satirlar: SabitGelirSatir[]
    uyarilar: string[]
    mevcutSiparisId?: string | null
    mevcutSiparisRefNo?: string | null
    toplamTutar: number
    toplamKdv: number
}

export interface SabitGelirSiparis {
    ambarId: string
    refNo?: string | null
    customerId?: string | null
    customerName?: string | null
    poNo?: string | null
    tur?: number | null
    donemYil?: number | null
    donemAy?: number | null
    orderDateStr?: string | null
    satirlar: SabitGelirSatir[]
    faturalanmisSatirVar: boolean
}

export interface SabitGelirSiparisIstegi {
    customerId?: string
    poNo?: string
    tur: number
    donemYil: number
    donemAy: number
}

export interface KaydetSabitGelirKartiIstegi {
    sabitGelirKartiId?: string
    tur: number
    ad?: string
    ambarAgreementSchemeId?: string
    personnelId?: string
    startDate?: string
    endDate?: string
    isCustomActive: boolean
    dagitimlar: { companyPoId?: string, oran?: number }[]
}

interface Yanit<T> { isSuccess: boolean, message?: string, data: T }

const cagir = async <T>(uc: string, govde: any = {}): Promise<Yanit<T>> => {
    try {
        console.log('SERVICE CALL - SabitGelir/' + uc, JSON.stringify(govde));
        const resp = await axios.post(Helpers.getEndpoint() + '/SabitGelir/' + uc, govde);
        return Helpers.dataParser(resp);
    } catch (error) {
        throw Helpers.parseErrorMessage(error);
    }
}

export class SabitGelirService {
    static getList = () => cagir<SabitGelirKarti[]>('GetList');
    static getListPagingAndFilter = (istek: SabitGelirListIstegi) => cagir<SabitGelirListSonucu>('GetListPagingAndFilter', istek);
    static exportList = (istek: SabitGelirListIstegi) => cagir<string>('ExportList', istek);
    static getById = (id: string) => cagir<SabitGelirKarti>('GetById', { id });
    static kaydet = (istek: KaydetSabitGelirKartiIstegi) => cagir<SabitGelirKarti>('Kaydet', istek);
    static sil = (ids: string[]) => cagir<boolean>('Sil', { ids });
    static poSatirlari = () => cagir<SabitGelirPoSatiri[]>('PoSatirlari');
    static sablonlar = () => cagir<SabitGelirSablon[]>('Sablonlar');
    static poNumaralari = (customerId: string, tur?: number) => cagir<string[]>('PoNumaralari', { customerId, tur });
    static hesapla = (istek: SabitGelirSiparisIstegi) => cagir<SabitGelirHesapSonucu>('Hesapla', istek);
    static siparisOlustur = (istek: SabitGelirSiparisIstegi) => cagir<SabitGelirSiparis>('SiparisOlustur', istek);
    static siparisYenile = (ambarId: string) => cagir<SabitGelirSiparis>('SiparisYenile', { id: ambarId });
    static getSiparis = (ambarId: string) => cagir<SabitGelirSiparis>('GetSiparis', { id: ambarId });

    /** CustomSingleSelect için {label, value} listesine çevirir. */
    static secenek = async <T>(istek: Promise<Yanit<T[]>>, cevir: (x: T) => { label: string, value: string, additionalValue1?: string }) => {
        const r = await istek;
        return { ...r, data: r.isSuccess ? r.data.map(cevir) : [] };
    }
}
